import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { TimelineEvent, TimelineResponse } from "../api/types";
import { useAuth } from "../lib/auth/useAuth";
import { useRole } from "../lib/auth/useRole";
import { localMidnight } from "../lib/date";
import { latestRecordedAt } from "../lib/dayRecap";
import { getWorkerUrl } from "../lib/session";
import { useManagerTeam } from "./useManagerTeam";

const TIMELINE_PREFIX = ["tawa", "worker", "timeline"] as const;

/** Events de toutes les timelines déjà en cache (accueil, feuille de temps,
 * récap…), relus quand l'une d'elles reçoit des données ou quitte le cache.
 * Ne déclenche aucune requête. */
function useCachedTimelineEvents(): TimelineEvent[] {
  const qc = useQueryClient();
  const [, setVersion] = useState(0);
  useEffect(
    () =>
      qc.getQueryCache().subscribe((e) => {
        const changed =
          e.type === "removed" ||
          (e.type === "updated" && e.action.type === "success");
        if (!changed) return;
        const key = e.query.queryKey;
        if (TIMELINE_PREFIX.every((part, i) => key[i] === part)) {
          setVersion((v) => v + 1);
        }
      }),
    [qc],
  );
  return qc
    .getQueriesData<TimelineResponse>({ queryKey: [...TIMELINE_PREFIX] })
    .flatMap(([, data]) => data?.events ?? []);
}

/**
 * Fraîcheur affichée dans la barre du haut : la dernière réception (max
 * `recorded_at`) depuis hier 00:00, lue dans le cache, sans aucune requête.
 *
 * Worker : ses propres pointages. Manager ou admin : ceux de l'équipe
 * seulement, une fois /recap passé par là ; jamais les siens en repli, qui
 * masqueraient une remontée d'équipe en panne. null tant que rien n'est connu.
 */
export function useDataFreshness(now: Date = new Date()): Date | null {
  const { user } = useAuth();
  const role = useRole();
  const manages = role === "manager" || role === "admin";
  // Désactivée : ne lit que ce qu'un écran d'équipe a déjà chargé.
  const team = useManagerTeam(false);
  const since = localMidnight(now, -1).getTime();
  const recent = useCachedTimelineEvents().filter(
    (ev) => Date.parse(ev.occurred_at) >= since,
  );

  if (manages) {
    const teamSet = new Set(
      (team.data?.workers ?? []).map((w) => w.worker_url),
    );
    return latestRecordedAt(recent.filter((ev) => teamSet.has(ev.worker_url)));
  }
  let me = "";
  try {
    me = getWorkerUrl(user);
  } catch {
    me = "";
  }
  return latestRecordedAt(recent.filter((ev) => ev.worker_url === me));
}
